import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';

/**
 * Get module schedule data for a student's enrolled packages
 * @param {string} studentUid - Student's UID
 * @returns {Array} Array of modules with schedule information
 */
export const getModuleScheduleForStudent = async (studentUid) => {
    try {
        // Get student's enrolled packages
        const userDocRef = doc(db, "users", studentUid);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
            return [];
        }

        const userData = userDoc.data();
        const buyItemIdsArray = Array.isArray(userData.buyItemIdsArray) ? userData.buyItemIdsArray : [];
        const packageIds = buyItemIdsArray
            .filter(item => item && typeof item === 'object' && item.buyItemId)
            .map(item => item.buyItemId);

        if (packageIds.length === 0) {
            return [];
        }

        // Get package data
        const lmsPackageCollection = collection(db, "lms_package");
        const packageQuery = query(lmsPackageCollection, where("packageId", "in", packageIds));
        const packageSnapshot = await getDocs(packageQuery);
        
        const moduleSchedules = [];

        for (const packageDoc of packageSnapshot.docs) {
            const packageData = packageDoc.data();
            
            if (packageData.packageContainingBatches) {
                for (const batchId of packageData.packageContainingBatches) {
                    if (typeof batchId === 'string') {
                        // Get batch data
                        const batchRef = doc(db, "lms_batch", batchId);
                        const batchSnap = await getDoc(batchRef);
                        
                        if (batchSnap.exists()) {
                            const batchData = batchSnap.data();
                            
                            if (batchData.moduleSettings) {
                                for (const [moduleKey, moduleSetting] of Object.entries(batchData.moduleSettings)) {
                                    // Get module details
                                    const moduleRef = doc(db, "lms_module", moduleSetting.moduleId);
                                    const moduleSnap = await getDoc(moduleRef);
                                    
                                    if (moduleSnap.exists()) {
                                        const moduleData = moduleSnap.data();
                                        
                                        moduleSchedules.push({
                                            packageId: packageData.packageId,
                                            packageName: packageData.packageName,
                                            batchId: batchId,
                                            batchName: batchData.batchName,
                                            moduleId: moduleSetting.moduleId,
                                            moduleName: moduleSetting.moduleName || moduleData.moduleName,
                                            moduleType: moduleData.moduleType,
                                            liveLink: moduleData.liveLink,
                                            moduleStartDate: moduleSetting.moduleStartDate,
                                            moduleEndDate: moduleSetting.moduleEndDate,
                                            moduleInstallmentStatus: moduleSetting.moduleInstallmentStatus,
                                            moduleBanner: moduleData.moduleBanner,
                                            moduleDescription: moduleData.moduleDescription
                                        });
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        return moduleSchedules;
    } catch (error) {
        console.error('Error fetching module schedules:', error);
        return [];
    }
};

/**
 * Check if a live session is currently active
 * @param {Object} module - Module object with schedule data
 * @returns {Object} Live session status
 */
export const getLiveSessionStatus = (module) => {
    if (module.moduleType !== 'live' || !module.moduleStartDate || !module.moduleStartTime) {
        return {
            isLive: false,
            status: 'not_scheduled',
            message: 'No live session scheduled'
        };
    }

    const now = new Date();
    const moduleDate = new Date(module.moduleStartDate);
    
    // Parse time (assuming format like "14:30" or "2:30 PM")
    const timeStr = module.moduleStartTime;
    let [hours, minutes] = timeStr.split(':');
    
    // Handle AM/PM format
    if (timeStr.toLowerCase().includes('pm') && parseInt(hours) !== 12) {
        hours = parseInt(hours) + 12;
    } else if (timeStr.toLowerCase().includes('am') && parseInt(hours) === 12) {
        hours = 0;
    }
    
    const moduleStartDateTime = new Date(moduleDate);
    moduleStartDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    // Calculate end time (assuming 1 hour duration if no end time provided)
    const moduleEndDateTime = new Date(moduleStartDateTime);
    if (module.moduleEndTime) {
        let [endHours, endMinutes] = module.moduleEndTime.split(':');
        if (module.moduleEndTime.toLowerCase().includes('pm') && parseInt(endHours) !== 12) {
            endHours = parseInt(endHours) + 12;
        } else if (module.moduleEndTime.toLowerCase().includes('am') && parseInt(endHours) === 12) {
            endHours = 0;
        }
        moduleEndDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
    } else {
        moduleEndDateTime.setTime(moduleStartDateTime.getTime() + (60 * 60 * 1000)); // Add 1 hour
    }

    // Check status
    const timeDiffStart = moduleStartDateTime.getTime() - now.getTime();
    const timeDiffEnd = moduleEndDateTime.getTime() - now.getTime();
    
    if (timeDiffStart > 0) {
        // Session hasn't started yet
        const hoursUntilStart = Math.floor(timeDiffStart / (1000 * 60 * 60));
        const minutesUntilStart = Math.floor((timeDiffStart % (1000 * 60 * 60)) / (1000 * 60));
        
        return {
            isLive: false,
            status: 'upcoming',
            message: `Starts in ${hoursUntilStart}h ${minutesUntilStart}m`,
            startTime: moduleStartDateTime,
            endTime: moduleEndDateTime,
            canJoin: timeDiffStart <= (15 * 60 * 1000) // Allow joining 15 minutes before
        };
    } else if (timeDiffEnd > 0) {
        // Session is currently active
        const minutesRemaining = Math.floor(timeDiffEnd / (1000 * 60));
        
        return {
            isLive: true,
            status: 'active',
            message: `Live now - ${minutesRemaining}m remaining`,
            startTime: moduleStartDateTime,
            endTime: moduleEndDateTime,
            canJoin: true
        };
    } else {
        // Session has ended
        return {
            isLive: false,
            status: 'ended',
            message: 'Session ended',
            startTime: moduleStartDateTime,
            endTime: moduleEndDateTime,
            canJoin: false
        };
    }
};

/**
 * Get modules with live session status for student dashboard
 * @param {string} studentUid - Student's UID
 * @returns {Array} Array of modules with live session status
 */
export const getModulesWithLiveStatus = async (studentUid) => {
    try {
        const modules = await getModuleScheduleForStudent(studentUid);
        
        return modules.map(module => ({
            ...module,
            liveStatus: getLiveSessionStatus(module)
        }));
    } catch (error) {
        console.error('Error getting modules with live status:', error);
        return [];
    }
};